import { useState, useEffect } from 'react';
import { ApplicationApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { getLoanTypeShortName, onRowPrepared } from '../../loans/common/LoanFunctions';
import { getPaidColumnHtml, onDaysSinceAppliedColumnHtml } from '../../applications/common/ApplicationFunctions';
import { BfiDataGrid, Column, MasterDetail, formatDateMonthDayYear } from '@apply/ui';
import { ApplicationStatusTypeOptions, GridSettingType } from '@apply/constants';
import { Tooltip } from 'devextreme-react/tooltip';
import { addressTooltipText } from '../../common/SharedFunctions';
import ApplicationPortfolioTable from './ApplicationPortfolioTable';

const PrincipalApplicationsTable = ({ principalId, gridStore }) => {
    const applicationApi = new ApplicationApi.ApplicationApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);

    const dataFunction = (criteria) => applicationApi.getGrid_ByPrincipalId(principalId, criteria);

    useEffect(() => {
        if (gridStore && principalId) {
            gridStore.setOnLoad('id', dataFunction, null);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const onLoanTypeCellRender = (e) => {
        return getLoanTypeShortName(e.data.loanTypeId);
    };

    const onIsPaidCellRender = (e) => {
        return getPaidColumnHtml(e.value);
    };

    const onCityCellRender = (e) => {
        const targetId = `ac_${e.data.id}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyCity}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onStateCellRender = (e) => {
        const targetId = `as_${e.data.id}}`;
        const text = addressTooltipText(e.data.address);
        return (
            <>
                <div
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyState}
                </div>
                {!e.data.isPortfolio && (
                    <Tooltip
                        target={`[data-for="${targetId}"]`}
                        showEvent='mouseenter'
                        hideEvent='mouseleave'
                        hideOnOutsideClick={false}
                    >
                        {text}
                    </Tooltip>
                )}
            </>
        );
    };

    const onAppStatusRender = (e) => {
        if (!e.value) {
            return null;
        }
        return ApplicationStatusTypeOptions.find((s) => s.id === e.value)?.shortName;
    };

    const renderPortfolioRecords = (e) => {
        if (!e.data.isPortfolio) {
            return '';
        }
        return (
            <div style={{ padding: '10px 10px 10px 10px' }}>
                <ApplicationPortfolioTable data={e.data.portfolio} />
            </div>
        );
    };

    const onDateRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDateMonthDayYear(e.value);
    };

    if (!gridStore || !principalId) {
        return null;
    }

    return (
        <BfiDataGrid
            gridType={GridSettingType.PrincipalApplications}
            defaultPageSize={25}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            noDataText='No Applications found'
            onRowPrepared={onRowPrepared}
        >
            <Column
                dataField={'loanTypeId'}
                caption='Type'
                width={100}
                cellRender={onLoanTypeCellRender}
                alignment='left'
            />
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                dataType='string'
                width={100}
            />
            <Column
                dataField='propertyCity'
                caption='City'
                alignment='left'
                cellRender={onCityCellRender}
            />
            <Column
                dataField='propertyState'
                caption='State'
                width={60}
                alignment='left'
                cellRender={onStateCellRender}
            />
            <Column
                dataField='createdDate'
                caption='Created'
                width={100}
                cellRender={onDateRender}
                alignment='left'
            />
            <Column
                dataField='daysSinceApplication'
                caption='Applied'
                width={100}
                cellRender={onDaysSinceAppliedColumnHtml}
                alignment='center'
            />

            <Column
                dataField='isPaid'
                caption='Paid'
                width={70}
                cellRender={onIsPaidCellRender}
                alignment='center'
                cssClass='border-left'
            />
            <Column
                dataField='responsibleName'
                caption='POC'
                dataType='string'
                width={150}
            />
            <Column
                dataField='appStatusId'
                caption='Status'
                dataType='string'
                width={120}
                cellRender={onAppStatusRender}
            />
            <MasterDetail
                enabled={true}
                render={renderPortfolioRecords}
            />
        </BfiDataGrid>
    );
};

export default PrincipalApplicationsTable;
